import { useCallback, useEffect, useState } from "react"
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material"
import type { SelectChangeEvent } from "@mui/material"
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp"
import { Bar, BarChart, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts"
import { LoadingIcon } from "components/Common/LoadingIcon"
import { Title } from "components/Common/Title"
import { useOrganizations } from "../useOrganizations"
import analyticsService from "services/analyticsService"
import { hiabColors } from "theme"
import type { IAnalyticsTrainingUserDetail, IAnalyticsTrainingOrg, IAnalyticsUserLicense } from "types/analyticsTypes"

type ViewFilter = "bottom10" | "top10" | "all"

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "—"
  return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
}

function formatDuration(hours: number): string {
  const totalSeconds = Math.round(hours * 3600)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${minutes}m ${seconds}s`
}

function getLicenseStatus(license: IAnalyticsUserLicense): "Active" | "Expired" | "Cancelled" {
  if (license.licenseCanceled) return "Cancelled"
  if (license.cancellationDate !== null && new Date(license.cancellationDate) < new Date()) return "Expired"
  return "Active"
}

function applyFilter(orgs: IAnalyticsTrainingOrg[], filter: ViewFilter): IAnalyticsTrainingOrg[] {
  switch (filter) {
    case "top10":
      return [...orgs].sort((a, b) => b.activeUsers - a.activeUsers).slice(0, 10)
    case "bottom10":
      return [...orgs].sort((a, b) => a.activeUsers - b.activeUsers).slice(0, 10)
    case "all":
      return [...orgs].sort((a, b) => a.activeUsers - b.activeUsers) // least active first
  }
}

type UserDetailRowProps = {
  user: IAnalyticsTrainingUserDetail
  orgId: string
}

function UserDetailRow({ user, orgId }: UserDetailRowProps) {
  const [open, setOpen] = useState(false)
  const [licenses, setLicenses] = useState<IAnalyticsUserLicense[]>([])
  const [loadingLicenses, setLoadingLicenses] = useState(false)
  const [licensesLoaded, setLicensesLoaded] = useState(false)
  const [licenseError, setLicenseError] = useState<string | null>(null)

  const fetchLicenses = useCallback(async () => {
    setLoadingLicenses(true)
    setLicenseError(null)
    try {
      const result = await analyticsService.getTrainingUserLicensesForUser(orgId, user.userId)
      setLicenses(result)
      setLicensesLoaded(true)
    } catch {
      setLicenseError("Failed to load license history.")
    } finally {
      setLoadingLicenses(false)
    }
  }, [orgId, user.userId])

  const handleToggle = useCallback(() => {
    const nextOpen = !open
    setOpen(nextOpen)
    if (nextOpen && !licensesLoaded) {
      fetchLicenses()
    }
  }, [open, licensesLoaded, fetchLicenses])

  return (
    <>
      <TableRow sx={{ "& > *": { borderBottom: "unset" } }}>
        <TableCell sx={{ width: 48 }}>
          <IconButton size="small" onClick={handleToggle}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{user.userName}</TableCell>
        <TableCell align="right">{user.sessionCount}</TableCell>
        <TableCell align="right">{formatDuration(user.avgHoursPerSession)}</TableCell>
        <TableCell align="right">{user.lessonCount}</TableCell>
        <TableCell align="right">{formatDate(user.firstTraining)}</TableCell>
        <TableCell align="right">{formatDate(user.lastTraining)}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={7} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ m: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                License History
              </Typography>
              {loadingLicenses && <LoadingIcon />}
              {!loadingLicenses && licenseError && (
                <Alert
                  severity="error"
                  action={
                    <Button color="inherit" size="small" onClick={fetchLicenses}>
                      Retry
                    </Button>
                  }
                >
                  {licenseError}
                </Alert>
              )}
              {!loadingLicenses && !licenseError && licenses.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No licenses found for this user.
                </Typography>
              )}
              {!loadingLicenses && licenses.length > 0 && (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Pool Name</TableCell>
                      <TableCell>Assigned Date</TableCell>
                      <TableCell>Expiry Date</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {licenses.map((license) => {
                      const status = getLicenseStatus(license)
                      return (
                        <TableRow key={license.licenseId}>
                          <TableCell>{license.licensePoolName}</TableCell>
                          <TableCell>{formatDate(license.assignedDate)}</TableCell>
                          <TableCell>{formatDate(license.cancellationDate)}</TableCell>
                          <TableCell>
                            <Chip
                              label={status}
                              size="small"
                              color={
                                status === "Active" ? "success" : status === "Cancelled" ? "error" : "default"
                              }
                            />
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export function TrainingEngagementTab() {
  const [period, setPeriod] = useState(90)
  const [data, setData] = useState<IAnalyticsTrainingOrg[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filter, setFilter] = useState<ViewFilter>("bottom10")
  const [selectedDetailOrgId, setSelectedDetailOrgId] = useState<string | null>(null)
  const [detailData, setDetailData] = useState<IAnalyticsTrainingUserDetail[]>([])
  const [detailLoading, setDetailLoading] = useState(false)
  const [detailError, setDetailError] = useState<string | null>(null)

  const { organizations, error: orgError, refetch: refetchOrgs } = useOrganizations()

  const load = useCallback(async (p: number) => {
    setLoading(true)
    setError(null)
    try {
      const result = await analyticsService.getTraining(p)
      setData(result)
    } catch {
      setError("Failed to load training engagement data.")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load(period)
  }, [load, period])

  const loadDetail = useCallback(async (orgId: string, p: number) => {
    setDetailLoading(true)
    setDetailError(null)
    try {
      const result = await analyticsService.getTrainingDetail(orgId, p)
      setDetailData(result)
    } catch {
      setDetailError("Failed to load training detail data.")
    } finally {
      setDetailLoading(false)
    }
  }, [])

  useEffect(() => {
    if (selectedDetailOrgId) {
      loadDetail(selectedDetailOrgId, period)
    }
  }, [loadDetail, selectedDetailOrgId, period])

  const handleDetailOrgChange = (event: SelectChangeEvent<string>) => {
    setSelectedDetailOrgId(event.target.value || null)
  }

  const handlePeriodChange = (event: SelectChangeEvent<number>) => {
    setPeriod(event.target.value as number)
  }

  const handleFilterChange = (event: SelectChangeEvent<ViewFilter>) => {
    setFilter(event.target.value as ViewFilter)
  }

  const filtered = applyFilter(data, filter)
  const chartHeight = filter === "all" ? Math.max(300, data.length * 40) : Math.max(300, 10 * 40)

  const activeUsersData = filtered.map((org) => ({
    name: org.orgName,
    activeUsers: org.activeUsers,
  }))

  const trainingHoursData = filtered.map((org) => ({
    name: org.orgName,
    totalTrainingHours: Math.round(org.totalTrainingHours),
  }))

  return (
    <Box>
      {orgError && (
        <Alert severity="warning" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={refetchOrgs}>Retry</Button>}>
          {orgError}
        </Alert>
      )}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Period</InputLabel>
          <Select<number> value={period} label="Period" onChange={handlePeriodChange}>
            <MenuItem value={30}>Last 30 days</MenuItem>
            <MenuItem value={90}>Last 90 days</MenuItem>
            <MenuItem value={180}>Last 180 days</MenuItem>
            <MenuItem value={365}>Last 365 days</MenuItem>
            <MenuItem value={0}>All time</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Show</InputLabel>
          <Select<ViewFilter> value={filter} label="Show" onChange={handleFilterChange}>
            <MenuItem value="bottom10">Bottom 10 (least active)</MenuItem>
            <MenuItem value="top10">Top 10 (most active)</MenuItem>
            <MenuItem value="all">All ({data.length} orgs)</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {loading && <LoadingIcon />}

      {error && (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => load(period)}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {!loading && !error && (
        <Grid container spacing={3} direction="column">
          <Grid item>
            <Paper sx={{ p: 3 }}>
              <Title>ACTIVE USERS BY ORGANIZATION</Title>
              <Box sx={{ overflowY: filter === "all" ? "auto" : "visible", maxHeight: filter === "all" ? 500 : "none" }}>
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <BarChart data={activeUsersData} layout="vertical" margin={{ left: 120, right: 60 }}>
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis type="category" dataKey="name" width={120} />
                    <Tooltip />
                    <Bar dataKey="activeUsers" name="Active Users" fill={hiabColors.bluePrimary}>
                      <LabelList dataKey="activeUsers" position="right" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>

          <Grid item>
            <Paper sx={{ p: 3 }}>
              <Title>TRAINING HOURS BY ORGANIZATION</Title>
              <Box sx={{ overflowY: filter === "all" ? "auto" : "visible", maxHeight: filter === "all" ? 500 : "none" }}>
                <ResponsiveContainer width="100%" height={chartHeight}>
                  <BarChart data={trainingHoursData} layout="vertical" margin={{ left: 120, right: 60 }}>
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis type="category" dataKey="name" width={120} />
                    <Tooltip formatter={(v: number) => [`${v}h`, "Training Hours"]} />
                    <Bar dataKey="totalTrainingHours" name="Training Hours" fill={hiabColors.greyPrimary}>
                      <LabelList dataKey="totalTrainingHours" position="right" formatter={(v: number) => `${v}h`} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Per-org lesson detail */}
      {!loading && !error && (
        <Paper sx={{ p: 3, mt: 4 }}>
          <Title>TRAINING ACTIVITY BY USER</Title>

          <FormControl size="small" sx={{ minWidth: 240, mb: 1, mt: 1 }}>
            <InputLabel>Organization</InputLabel>
            <Select<string>
              value={selectedDetailOrgId ?? ""}
              label="Organization"
              onChange={handleDetailOrgChange}
            >
              {organizations.map((org) => (
                <MenuItem key={org.id} value={org.id}>
                  {org.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {!selectedDetailOrgId && (
            <Typography variant="body2" color="text.secondary">
              Select an organization to view lesson detail
            </Typography>
          )}

          {selectedDetailOrgId && detailLoading && <LoadingIcon />}

          {selectedDetailOrgId && detailError && (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => loadDetail(selectedDetailOrgId, period)}>
                  Retry
                </Button>
              }
            >
              {detailError}
            </Alert>
          )}

          {selectedDetailOrgId && !detailLoading && !detailError && (
            <>
              {detailData.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No training activity found for this organization in the selected period.
                </Typography>
              )}
              {detailData.length > 0 && (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ width: 48 }} />
                        <TableCell>User</TableCell>
                        <TableCell align="right">Training Sessions</TableCell>
                        <TableCell align="right">Avg Session Duration</TableCell>
                        <TableCell align="right">Lessons</TableCell>
                        <TableCell align="right">First Training</TableCell>
                        <TableCell align="right">Last Training</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detailData.map((row) => (
                        <UserDetailRow key={row.userId} user={row} orgId={selectedDetailOrgId} />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </Paper>
      )}
    </Box>
  )
}
